import { toEmsApiTimestamp } from "../Logic/emsDates";
import { MailDirection, MailProcessingSearchCriteria, ProcessingStatus } from "./EmailSearchAPI";

/** 201 is a confirmed 400, not a clamp, so the UI caps rather than relying on the server. */
export const MAX_PAGE_SIZE = 200;

/**
 * The two orderings the server actually supports.
 *
 * `sortColumn` accepts exactly `"status"`; every other value — including a
 * typo — is silently ignored and the rows come back newest-activity-first with
 * a `200`. Modelling the choice as a union of two names rather than a free
 * string means the UI cannot send a third value at all.
 */
export type EmailSortColumn = "status" | "lastActivity";

/**
 * The last-activity window, as the operator picked it rather than as a pair of
 * instants.
 *
 * A relative preset is stored as the CHOICE, not as the timestamp it resolved
 * to when it was clicked. `toCriteria` reads the clock every time it builds a
 * request, so "Last 3 hours" still means three hours ago on a refresh twenty
 * minutes later. Freezing it at selection time would leave an operator
 * watching a stale, silently-sliding-backwards window.
 *
 * `custom` is the only value under which `dateFrom`/`dateTo` are read.
 */
export type DateRangePreset = "any" | "3h" | "6h" | "12h" | "24h" | "7d" | "30d" | "custom";

/**
 * How far back each relative preset reaches, in hours.
 *
 * A `Record` over the relative presets only — `any` and `custom` are excluded
 * by the key type, so neither can be given a duration by accident and a new
 * preset cannot compile until it has one.
 */
const PRESET_HOURS: Record<Exclude<DateRangePreset, "any" | "custom">, number> = {
    "3h": 3,
    "6h": 6,
    "12h": 12,
    "24h": 24,
    "7d": 24 * 7,
    "30d": 24 * 30,
};

const MS_PER_HOUR = 60 * 60 * 1000;

/** What the controls hold. Converted to a wire request by `toCriteria`. */
export interface EmailFilters {
    direction: MailDirection;
    failedOnly: boolean;
    statuses?: ProcessingStatus[];
    mailboxID?: number;
    /** The window the operator chose. Only `custom` reads the two dates below. */
    dateRange: DateRangePreset;
    /** Custom range only, and only ever set by the two date inputs. */
    dateFrom?: string;
    dateTo?: string;
    text?: string;
    /**
     * Lives here, unlike the page number, which `toCriteria` takes separately:
     * page is transient state the hook owns and every filter change resets,
     * while page size is a property of the query. Nothing varies it yet — there
     * is no page-size control — so it is `DEFAULT_FILTERS`' 50 throughout.
     */
    itemsPerPage: number;
    sortBy: EmailSortColumn;
    sortAscending: boolean;
}

/**
 * The worklist's opening state.
 *
 * Failures only, newest first. The page exists to answer "what is broken in
 * this tenant", and an operator who types nothing must still get that answer —
 * which is why this is a worklist rather than an empty search box.
 */
export const DEFAULT_FILTERS: EmailFilters = {
    direction: "Inbound",
    failedOnly: true,
    dateRange: "any",
    itemsPerPage: 50,
    sortBy: "lastActivity",
    sortAscending: false,
};

/** `clampPage` and `toCriteria` both refuse to go below this. */
function atLeastOne(page: number): number {
    return page < 1 ? 1 : page;
}

/**
 * The last reachable page for a result set of this size.
 *
 * An empty set (`totalCount <= 0`) still reads as one page — "Page 1 of 1" —
 * rather than "Page 1 of 0", which would make the pager's own display look
 * broken on the worklist's own empty state. Shared by `clampPage` (which page
 * is reachable) and the pager display (which page is last): both need the
 * same arithmetic, so it lives in exactly one place.
 */
/**
 * The last reachable page, which is 1 for an empty result set rather than 0.
 *
 * Shared with `Pager` so the two cannot drift: the pager needs "which page is
 * last" and `clampPage` needs "which page is reachable", and those are the
 * same ceiling division. Assumes `itemsPerPage >= 1` — there is no page-size
 * control anywhere in this feature, so it is always `DEFAULT_FILTERS`' 50, and
 * a 0 would give `Infinity` rather than throwing.
 */
export function lastPageFor(totalCount: number, itemsPerPage: number): number {
    return totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : 1;
}

/**
 * Pulls a page number back into range for a result set that has shrunk.
 *
 * Paging past the end is not an error — page 99 returns `200`, an empty
 * `items`, and the true `totalCount`. Without this, narrowing a filter while
 * on page 5 leaves an operator on an empty table with nothing explaining why.
 */
export function clampPage(page: number, totalCount: number, itemsPerPage: number): number {
    return atLeastOne(Math.min(page, lastPageFor(totalCount, itemsPerPage)));
}

/**
 * Turns the chosen window into the two wire values.
 *
 * The custom inputs are read for `custom` and ignored otherwise, so a leftover
 * date from an earlier custom range cannot leak into a preset search — the
 * inputs deliberately keep their value while a preset is selected so that
 * switching back to Custom restores what was there.
 *
 * A relative preset sends no `dateTo`. Pinning the end to the instant the
 * request was built would drop any row whose last activity lands between that
 * instant and the server reading its own clock, which on a worklist watching a
 * queue drain is precisely the row being waited for.
 *
 * `toEmsApiTimestamp` — not `toISOString` — because the server compares these
 * against stored values that are offset-less server-local time (contract
 * §1.3). Sending UTC would shift every window by two hours and return the
 * wrong rows with a 200.
 */
function resolveDates(filters: EmailFilters, now: Date): { dateFrom?: string; dateTo?: string } {
    if (filters.dateRange === "any") {
        return {};
    }
    if (filters.dateRange === "custom") {
        // `|| undefined` on STRINGS, not the banned `|| 0` on a number: a
        // cleared `<input type="date">` reads as "", and a blank is dropped
        // rather than sent as whitespace. Task 7's control guards this too;
        // the safety net belongs on this side so a future caller cannot skip it.
        return { dateFrom: filters.dateFrom || undefined, dateTo: filters.dateTo || undefined };
    }
    return { dateFrom: toEmsApiTimestamp(new Date(now.getTime() - PRESET_HOURS[filters.dateRange] * MS_PER_HOUR)) };
}

/**
 * Turns the controls into a request that CANNOT be rejected.
 *
 * This is the error strategy. `searchErrors` exists for the case that gets
 * through anyway, but every rule below closes off a confirmed `400` or a
 * confirmed silent wrong answer:
 *
 * - `failedOnly` is dropped when explicit statuses are chosen, because the two
 *   AND together on the server and an operator could otherwise build a
 *   contradiction that silently returns nothing.
 * - `itemsPerPage` is capped at 200; 201 is a `400`.
 * - `pageNumber` is never below 1.
 * - `sortColumn` is sent only for the one value the server recognises.
 * - A blank `text` is omitted rather than sent as whitespace.
 * - The date window is resolved from the preset here, at request time — see
 *   `resolveDates` and `DateRangePreset`.
 *
 * `now` is a seam for the tests and defaults to the real clock. It is a
 * parameter default rather than a module constant on purpose: evaluated per
 * call, so a relative window slides forward with every refresh instead of
 * freezing at whatever time the bundle was parsed.
 */
export function toCriteria(filters: EmailFilters, page: number, now: Date = new Date()): MailProcessingSearchCriteria {
    const useExplicitStatuses = filters.statuses !== undefined && filters.statuses.length > 0;
    const text = filters.text?.trim();
    const { dateFrom, dateTo } = resolveDates(filters, now);

    return {
        direction: filters.direction,
        // Mutually exclusive, never both. See the doc comment above.
        statuses: useExplicitStatuses ? filters.statuses : undefined,
        // `|| undefined` on a BOOLEAN, which is not the banned `|| 0` on a
        // number: `false` here means the switch is off, and an off switch is
        // omitted rather than sent as `false`.
        failedOnly: useExplicitStatuses ? undefined : filters.failedOnly || undefined,
        mailboxID: filters.mailboxID,
        dateFrom,
        dateTo,
        text: text ? text : undefined,
        pageNumber: atLeastOne(page),
        itemsPerPage: Math.min(filters.itemsPerPage, MAX_PAGE_SIZE),
        sortColumn: filters.sortBy === "status" ? "status" : undefined,
        sortAscending: filters.sortAscending,
    };
}

/**
 * The filters that survive a direction switch, and the ones that do not.
 *
 * Direction is a MODE SWITCH, not a filter: the API requires it and it selects
 * the row shape, so there is no "both" to fall back to. The meaningful status
 * set differs by direction — `Abandoned` is a send-give-up state that exists
 * only outbound — so the status filter is dropped. Mailbox, the date window
 * and text mean the same thing either way and are kept.
 *
 * Dropping a specific status falls back to the failure set rather than to
 * "any status". The two controls are one select now, so a bare `statuses:
 * undefined` would leave that select reading "Any status" — silently widening
 * a one-status search to every email in the mailbox, on a page whose whole
 * purpose is to open on what is broken. A deliberate "Any status" (no
 * statuses, failure switch already off) is left exactly as it is: nothing is
 * being dropped there, so there is nothing to fall back from.
 */
export function forDirection(filters: EmailFilters, direction: MailDirection): EmailFilters {
    const droppingAStatus = filters.statuses !== undefined && filters.statuses.length > 0;
    return {
        ...filters,
        direction,
        statuses: undefined,
        failedOnly: droppingAStatus ? true : filters.failedOnly,
    };
}

/**
 * The filters that survive a tenant switch.
 *
 * Only `mailboxID` is dropped, and it must be: mailbox ids are per-tenant, so
 * a carried-over id silently filters every row out. `mailboxID: 1` returns
 * nothing in the dev tenant, whose ids run 57–66.
 */
export function forTenant(filters: EmailFilters): EmailFilters {
    return { ...filters, mailboxID: undefined };
}

/**
 * Which direction a column opens in on its first click.
 *
 * A `Record` over `EmailSortColumn`, so a third ordering cannot compile until
 * someone decides which way it reads. Last activity opens newest-first, which
 * is what a worklist wants and is already `DEFAULT_FILTERS`; status opens
 * A-to-Z, because a status list is read top-down. One shared direction would
 * have opened one of the two columns backwards on every first click.
 */
const OPENS_ASCENDING: Record<EmailSortColumn, boolean> = {
    status: true,
    lastActivity: false,
};

/**
 * The filters after a click on a sortable column header.
 *
 * Clicking the column already sorted on flips its direction; clicking any
 * other column switches to it in that column's own opening direction.
 *
 * There is deliberately no third "unsorted" state to cycle into. Omitting
 * `sortColumn` IS last-activity descending on the server (§3.5) rather than an
 * absence of ordering, so a cleared state would be a second name for a sort
 * the table would then be showing no marker for — rows in an order with
 * nothing on screen saying what it is.
 *
 * Page is not reset here: `useEmailSearch.setFilters` already does that for
 * every filter change, and a re-ordered result set makes page 4 meaningless in
 * exactly the way it does for a narrowed one.
 */
export function withSort(filters: EmailFilters, column: EmailSortColumn): EmailFilters {
    if (filters.sortBy === column) {
        return { ...filters, sortAscending: !filters.sortAscending };
    }
    return { ...filters, sortBy: column, sortAscending: OPENS_ASCENDING[column] };
}
